import React from 'react';
import {
  Button,
  ButtonOwnProps,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  PopoverOrigin,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { FooterAction } from '../../../interfaces';

interface Props {
  id?: string;
  options: (FooterAction & {
    id?: string;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    dividerBefore?: boolean;
  })[];
  selectedOption?: string;
  button?: {
    content?: React.ReactNode;
    sx?: SxProps<Theme>;
    variant?: ButtonOwnProps['variant'];
    keepDefaultStyles?: boolean;
    horizontal?: boolean;
    disableRipple?: boolean;
  };
  disabled?: boolean;
  center?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  isLoading?: boolean;
  paperSx?: SxProps<Theme>;
}

function DropdownMenu({
  id: propId,
  options,
  selectedOption,
  button,
  disabled,
  center,
  anchorOrigin,
  transformOrigin,
  isLoading,
  paperSx,
}: Props) {
  const theme = useTheme();
  const [idString] = React.useState(
    propId || Math.random().toString(36).substring(7),
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = `dropdown-button-${idString}`;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const optionalOptions = React.useMemo(() => {
    const tempOptions: Partial<React.ComponentProps<typeof Menu>> = {};
    if (anchorOrigin) tempOptions.anchorOrigin = anchorOrigin;
    if (transformOrigin) tempOptions.transformOrigin = transformOrigin;
    return tempOptions;
  }, [anchorOrigin, transformOrigin]);
  const Ellipsis = button?.horizontal ? MoreHoriz : MoreVert;

  return (
    <>
      <Button
        variant={button?.variant}
        id={id}
        disabled={disabled || isLoading}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        style={
          button?.keepDefaultStyles
            ? {}
            : {
                color: 'black',
                padding: 0,
                margin: 0,
                minWidth: 0,
              }
        }
        sx={button?.sx}
        disableRipple={button?.disableRipple}
        onClick={handleClick}>
        {button?.content ??
          (isLoading ? (
            <CircularProgress color="primary" size={16} />
          ) : (
            <Ellipsis />
          ))}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose as any}
        MenuListProps={{
          'aria-labelledby': id,
        }}
        PaperProps={paperSx ? { sx: paperSx } : undefined}
        {...optionalOptions}>
        {options?.flatMap(option => [
          option.dividerBefore ? (
            <Divider
              key={`${option.label?.toString()}-divider`}
              sx={{ my: '6px' }}
            />
          ) : null,
          <MenuItem
            key={option.label?.toString()}
            disabled={option.disabled}
            onClick={e => {
              if (option.disabled) return;
              handleMenuClose(e);
              option.action();
            }}
            sx={{
              textAlign: center ? 'center' : 'left',
              justifyContent: center ? 'center' : 'flex-start',
              bgcolor:
                option.id && selectedOption && selectedOption === option.id
                  ? theme.palette.primary.light
                  : undefined,
              ...option.sx,
            }}>
            {option.label}
            {option.coachMark}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}

export default DropdownMenu;
